import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAuth } from '../auth/AuthContext';
import BrandLogo from './BrandLogo';
import { authService } from '../api/api';

const ALLOWED_SIGNUP_DOMAIN = 'biomedwaste.com';

export default function AuthPage() {
  const { login, signup } = useAuth();
  const [tab, setTab] = useState('login');
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
  });

  const update = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const requestPasswordReset = async () => {
    setError('');
    setResetMessage('');
    if (!isAllowedSignupEmail(form.email)) {
      setResetMessage(`Enter your @${ALLOWED_SIGNUP_DOMAIN} email first.`);
      return;
    }
    try {
      setResetSaving(true);
      const res = await authService.requestPasswordReset({ email: form.email });
      setResetMessage(res.data?.message || 'If that account exists, a reset email has been sent.');
    } catch (err) {
      setResetMessage(err?.response?.data?.error || err?.message || 'Failed to request password reset.');
    } finally {
      setResetSaving(false);
    }
  };

  const isAllowedSignupEmail = (email) =>
    String(email || '').trim().toLowerCase().endsWith(`@${ALLOWED_SIGNUP_DOMAIN}`);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (tab === 'signup' && !isAllowedSignupEmail(form.email)) {
      setError(`Only @${ALLOWED_SIGNUP_DOMAIN} email addresses can sign up.`);
      return;
    }
    try {
      setSaving(true);
      if (tab === 'signup') {
        await signup(form);
      } else {
        await login(
          { email: form.email, password: form.password },
          { rememberMe },
        );
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Authentication failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card sx={{
        width: '100%',
        maxWidth: 460,
        borderRadius: 4,
        boxShadow: '0 24px 80px rgba(0,31,63,0.28)',
        animation: 'biofleetFadeUp 520ms ease-out both',
      }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <BrandLogo
              height={{ xs: 172, sm: 210 }}
              showText={false}
              logoGlow
              sx={{
                justifyContent: 'center',
                mb: 1.5,
                animation: 'biofleetLogoReveal 760ms cubic-bezier(0.2, 0.9, 0.25, 1.15) both',
                '& img': {
                  transition: 'transform 260ms ease, filter 260ms ease',
                  '&:hover': { transform: 'scale(1.045) rotate(-1deg)' },
                },
              }}
            />
            <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
              Sign in to manage vehicles, parts, reminders, and documents
            </Typography>
          </Box>

          <Tabs value={tab} onChange={(_e, next) => { setTab(next); setError(''); }} variant="fullWidth" sx={{ mb: 3 }}>
            <Tab value="login" icon={<LockIcon />} iconPosition="start" label="Login" />
            <Tab value="signup" icon={<PersonAddIcon />} iconPosition="start" label="Sign Up" />
          </Tabs>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {tab === 'signup' && (
            <Alert severity="info" sx={{ mb: 2, fontWeight: 700 }}>
              Signups are restricted to BioMed employees with @{ALLOWED_SIGNUP_DOMAIN} emails.
            </Alert>
          )}

          <Box component="form" onSubmit={submit}>
            {tab === 'signup' && (
              <TextField
                fullWidth
                required
                label="Full Name"
                value={form.name}
                onChange={update('name')}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth
              required
              type="email"
              label="Email"
              value={form.email}
              onChange={update('email')}
              helperText={tab === 'signup' ? `Use your @${ALLOWED_SIGNUP_DOMAIN} email.` : ''}
              error={tab === 'signup' && Boolean(form.email) && !isAllowedSignupEmail(form.email)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Password"
              value={form.password}
              onChange={update('password')}
              helperText={tab === 'signup' ? 'Minimum 8 characters. First signup becomes Global Admin; later signups start as Driver.' : ''}
              sx={{ mb: 3 }}
            />
            {tab === 'login' && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: -2, mb: 2 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={rememberMe}
                      onChange={(event) => setRememberMe(event.target.checked)}
                      size="small"
                    />
                  )}
                  label="Remember me"
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontWeight: 700 } }}
                />
                <Button size="small" startIcon={<LockResetIcon />} onClick={() => { setResetOpen(true); setResetMessage(''); }} sx={{ fontWeight: 900 }}>
                  Forgot password?
                </Button>
              </Box>
            )}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ py: 1.25, borderRadius: 2, fontWeight: 900, bgcolor: '#001f3f' }}
            >
              {saving ? <CircularProgress size={22} color="inherit" /> : tab === 'signup' ? 'Create Account' : 'Login'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>Forgot Password</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 2, fontWeight: 700 }}>
            Enter your BioFleet work email. We will send a secure reset link if the account exists.
          </Typography>
          <TextField
            fullWidth
            type="email"
            label="Work Email"
            value={form.email}
            onChange={update('email')}
            helperText={`Use your @${ALLOWED_SIGNUP_DOMAIN} email.`}
          />
          {resetMessage && <Alert severity={resetMessage.startsWith('Enter') || resetMessage.startsWith('Failed') ? 'warning' : 'success'} sx={{ mt: 2 }}>{resetMessage}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResetOpen(false)}>Close</Button>
          <Button variant="contained" disabled={resetSaving} onClick={requestPasswordReset} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            {resetSaving ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
